import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

type SaveState = "idle" | "saving" | "saved" | "error";

export interface AutosaveInputProps {
  label: string;
  value: string;
  placeholder?: string;
  type?: string;
  inputMode?: string;
  onChange: (value: string) => void;
  onSave: (value: string | null) => Promise<void> | void;
  validate?: (value: string) => string | null;
  allowClear?: boolean;
}

const AutosaveInput = ({
  label,
  value,
  placeholder,
  type = "text",
  inputMode,
  onChange,
  onSave,
  validate,
  allowClear = true,
}: AutosaveInputProps) => {
  const [status, setStatus] = useState<SaveState>("idle");
  const [error, setError] = useState<string | null>(null);

  const handleBlur = async () => {
    const trimmed = String(value ?? "").trim();
    // Allow clearing: empty string saves null
    if (allowClear && trimmed === "") {
      try {
        setStatus("saving");
        setError(null);
        await onSave(null);
        setStatus("saved");
        window.setTimeout(() => setStatus("idle"), 1500);
      } catch (e: any) {
        setStatus("error");
        setError(String(e?.message || "Save failed"));
      }
      return;
    }
    const msg = validate ? validate(value) : null;
    if (msg) {
      setStatus("error");
      setError(msg);
      return;
    }
    try {
      setStatus("saving");
      setError(null);
      await onSave(value);
      setStatus("saved");
      window.setTimeout(() => setStatus("idle"), 1500);
    } catch (e: any) {
      setStatus("error");
      setError(String(e?.message || "Save failed"));
    }
  };

  return (
    <div>
      <div className="text-xs text-muted-foreground mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <Input
          placeholder={placeholder}
          type={type}
          inputMode={inputMode as any}
          value={value}
          className="placeholder:text-muted-foreground/60 placeholder:italic"
          onChange={e => {
            if (error) setError(null);
            onChange(e.target.value);
          }}
          onBlur={handleBlur}
          onKeyDown={e => {
            if (e.key === "Enter") {
              e.preventDefault();
              (e.currentTarget as HTMLInputElement).blur();
            }
          }}
        />
        {allowClear && value && value.length > 0 ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label="Clear"
            onClick={async () => {
              onChange("");
              // Trigger save directly with null instead of relying on blur
              try {
                setStatus("saving");
                setError(null);
                await onSave(null);
                setStatus("saved");
                window.setTimeout(() => setStatus("idle"), 1500);
              } catch (e: any) {
                setStatus("error");
                setError(String(e?.message || "Save failed"));
              }
            }}
          >
            ×
          </Button>
        ) : null}
      </div>
      <div className={`text-xs mt-1 ${error ? "text-destructive" : "text-muted-foreground"}`}>
        {error ? error : status === "saving" ? "Saving…" : status === "saved" ? "Saved" : "\u00A0"}
      </div>
    </div>
  );
};

export default AutosaveInput;
